/**
 * Captures a single JPEG frame from a video at a given timestamp.
 * @param {string} videoUrl - The URL of the video.
 * @param {number} time - Seek time in seconds.
 * @returns {Promise<string>} - Base64 data URL (JPEG) of the frame.
 */
export const captureVideoFrame = (videoUrl, time = 0) => {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        if (videoUrl && !videoUrl.startsWith('blob:') && !videoUrl.startsWith('data:')) {
            video.crossOrigin = 'anonymous';
        }
        video.src = videoUrl;
        video.muted = true;
        video.preload = 'metadata';

        const timeout = setTimeout(() => reject(new Error('captureVideoFrame timeout')), 8000);

        video.onloadedmetadata = async () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth || 640;
                canvas.height = video.videoHeight || 360;
                const ctx = canvas.getContext('2d');

                const seekTime = Math.max(0, Math.min(time, video.duration - 0.05));
                video.currentTime = seekTime;
                await new Promise(r => { video.onseeked = r; });

                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                clearTimeout(timeout);
                resolve(canvas.toDataURL('image/jpeg', 0.75));
                video.src = '';
                video.remove();
            } catch (err) {
                clearTimeout(timeout);
                reject(err);
            }
        };

        video.onerror = () => {
            clearTimeout(timeout);
            reject(new Error('captureVideoFrame: failed to load video'));
        };
    });
};

/**
 * Extracts start and end frames from a video URL, and returns the actual video duration.
 *
 * The `durationHint` parameter is only used as a fallback when the browser cannot
 * determine the video duration from metadata (rare, but happens with some AI-generated
 * formats). The end frame always seeks to the TRUE end of the video (actual duration),
 * not a caller-supplied estimate — this is the correct behaviour for "Export End Frame".
 *
 * @param {string} videoUrl   - The URL of the video.
 * @param {number} durationHint - Fallback duration (seconds) if metadata is unavailable.
 * @returns {Promise<{startFrame: string, endFrame: string, duration: number}>}
 */
/**
 * Exports a trimmed segment of a video as an MP4 blob using WebCodecs + mp4-muxer.
 * Decodes frames from `offset` to `offset + duration`, re-encodes, and returns a
 * downloadable Blob.
 *
 * @param {string} videoUrl  - Source video URL.
 * @param {number} offset    - Start time in the source video (seconds).
 * @param {number} duration  - Length of the trimmed clip (seconds).
 * @param {function} [onProgress] - Optional callback(pct: 0-100).
 * @returns {Promise<Blob>} - MP4 Blob of the trimmed clip.
 */
export const exportTrimmedClip = async (videoUrl, offset = 0, duration = 4, onProgress) => {
    if (typeof VideoEncoder === 'undefined') {
        throw new Error('Your browser does not support WebCodecs. Use Chrome or Edge.');
    }
    const { Muxer, ArrayBufferTarget } = await import('mp4-muxer');

    const FPS = 30;

    // Load the source video
    const video = document.createElement('video');
    if (videoUrl && !videoUrl.startsWith('blob:') && !videoUrl.startsWith('data:')) {
        video.crossOrigin = 'anonymous';
    }
    video.muted = true;
    video.preload = 'auto';
    video.src = videoUrl;
    await new Promise((res, rej) => { video.onloadedmetadata = res; video.onerror = rej; });

    const w = video.videoWidth  || 1280;
    const h = video.videoHeight || 720;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');

    // Muxer + encoder
    const muxer = new Muxer({
        target: new ArrayBufferTarget(),
        video: { codec: 'avc', width: w, height: h },
        fastStart: 'in-memory',
    });
    const encoder = new VideoEncoder({
        output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
        error: (e) => console.error('VideoEncoder error:', e),
    });
    encoder.configure({
        codec: 'avc1.4d0028',
        width: w, height: h,
        bitrate: 4_000_000,
        framerate: FPS,
    });

    const totalFrames = Math.ceil(duration * FPS);
    const endTime     = offset + duration;

    for (let i = 0; i < totalFrames; i++) {
        const t = Math.min(offset + i / FPS, endTime - 0.01);
        video.currentTime = t;
        await new Promise(r => { video.onseeked = r; });

        ctx.drawImage(video, 0, 0, w, h);
        const frame = new VideoFrame(canvas, { timestamp: (i / FPS) * 1_000_000 });
        encoder.encode(frame, { keyFrame: i % (FPS * 2) === 0 });
        frame.close();

        if (onProgress) onProgress(Math.round((i / totalFrames) * 100));
    }

    await encoder.flush();
    encoder.close();
    muxer.finalize();
    video.src = '';
    video.remove();

    if (onProgress) onProgress(100);
    return new Blob([muxer.target.buffer], { type: 'video/mp4' });
};

export const extractVideoFrames = (videoUrl, durationHint = 4) => {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        // Only set crossOrigin for remote URLs — blob/data URLs don't need it
        // and setting it can cause silent failures on some browsers
        if (videoUrl && !videoUrl.startsWith('blob:') && !videoUrl.startsWith('data:')) {
            video.crossOrigin = 'anonymous';
        }
        video.src = videoUrl;
        video.muted = true;
        video.preload = 'metadata';

        const timeout = setTimeout(() => reject(new Error('Video load timeout')), 10000);

        video.onloadedmetadata = async () => {
            try {
                const canvas = document.createElement('canvas');
                const ctx    = canvas.getContext('2d');
                canvas.width  = video.videoWidth  || 640;
                canvas.height = video.videoHeight || 360;

                // Prefer the browser-reported duration; fall back to caller hint.
                const vidDuration = (isFinite(video.duration) && video.duration > 0)
                    ? video.duration
                    : (durationHint || 4);

                // ── 1. Start frame — seek to a small offset (not t=0) ────────
                // Many encoders write a black/green frame at the very start as
                // codec priming. Seeking to ~5% of duration (capped between
                // 0.1s and 0.5s) skips the priming so we always pull a real
                // frame. Falls back to t=0 only when the video is genuinely
                // shorter than 0.1s.
                const startSeek = Math.min(0.5, Math.max(0.1, vidDuration * 0.05));
                video.currentTime = vidDuration > 0.1 ? startSeek : 0;
                await new Promise(r => { video.onseeked = r; });
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                const startFrame = canvas.toDataURL('image/jpeg', 0.80);

                // ── 2. End frame — seek to 95 % of ACTUAL video length ───────
                // Using the true vidDuration (not the caller's hint) guarantees
                // we always capture a frame near the real end of the animation,
                // regardless of the clip's display duration setting.
                // We stop at 95 % to avoid the codec black/green frame that some
                // encoders append in the last few milliseconds.
                const endSeek = Math.max(0, vidDuration * 0.95);
                video.currentTime = endSeek;
                await new Promise(r => { video.onseeked = r; });
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                const endFrame = canvas.toDataURL('image/jpeg', 0.80);

                clearTimeout(timeout);
                // Return duration so callers can set maxDuration / clip duration correctly.
                resolve({ startFrame, endFrame, duration: vidDuration });

                video.src = '';
                video.remove();
            } catch (err) {
                clearTimeout(timeout);
                reject(err);
            }
        };

        video.onerror = () => {
            clearTimeout(timeout);
            reject(new Error('Failed to load video'));
        };
    });
};
